import { Injectable, OnDestroy } from '@angular/core';
import { Calendar, CalendarType } from '@vendasta/meetings';
import { forkJoin, map, Observable, of, shareReplay, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { GROUP, SERVICE } from '../../constants';
import { MeetingListSharedDataService } from '../shared/shared-table-view.service';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { TranslateService } from '@ngx-translate/core';
// @ts-expect-error Angular 21 doesn't like imports past the base package
import { EventTypeSummary } from '@vendasta/meetings/lib/_internal/objects/meeting-host';
import { DurationPipe } from '@galaxy/crm/integrations/meeting-event/src/pipes';

@Injectable()
export class MeetingListLoadDataService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private allEventTypes$: Observable<EventTypeSummary[]>;
  personalCalendar$: Observable<Calendar>;
  groupCalendars$: Observable<Calendar[]>;
  allGroupCalendarIds$: Observable<string[]>;
  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly sharedDataService: MeetingListSharedDataService,
    private readonly translate: TranslateService,
  ) {}

  getEventTypeData() {
    this.sharedDataService.setPersonalDefaultCalendar(this.personalCalendar$);
    this.sharedDataService.setGroupCalendar(this.groupCalendars$);

    this.allGroupCalendarIds$ = this.groupCalendars$.pipe(
      map((calendars) => calendars?.map((calendar) => calendar.id) || []),
      startWith([]),
    );

    this.allGroupCalendarIds$
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((calendarIds) =>
          calendarIds.length
            ? forkJoin(
                calendarIds.map((calendarId) => {
                  return this.meetingSchedulerStoreService.getGroupCalendarDataFromState(calendarId)
                    ? of(null)
                    : this.meetingSchedulerStoreService.loadCalendar(calendarId);
                }),
              )
            : of([]),
        ),
      )
      .subscribe();

    return this.allEventTypes$.pipe(
      map((eventTypes) =>
        eventTypes.map((et) => ({
          ...et,
          meetingTypeSlug: et.slug,
          duration: DurationPipe.transformDuration(`${et.duration}s`),
          eventType:
            et.calendarType === CalendarType.CALENDAR_TYPE_PERSONAL
              ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENT')
              : et.calendarType === CalendarType.CALENDAR_TYPE_GROUP
                ? this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENT')
                : this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.SESSION_EVENT'),
        })),
      ),
      shareReplay(1),
    );
  }

  loadGroupOrServiceData(selectedToggle): Observable<any[]> {
    this.personalCalendar$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.groupCalendars$ = this.meetingSchedulerStoreService.loadGroupCalendars();

    let data$: Observable<any[]>;
    switch (selectedToggle) {
      case GROUP:
        this.allEventTypes$ = of([]);
        data$ = this.meetingSchedulerStoreService.loadGroups();
        break;
      case SERVICE:
        this.allEventTypes$ = of([]);
        data$ = this.meetingSchedulerStoreService.loadServices();
        break;
      default:
        this.allEventTypes$ = this.meetingSchedulerStoreService.loadAllEventTypes().pipe(shareReplay(1));
        data$ = this.getEventTypeData();
    }
    return this.sortData(data$);
  }

  sortData(data$: Observable<any[]>): Observable<any[]> {
    return data$.pipe(
      map((data) => {
        if (Array.isArray(data)) {
          return this.applySorting(data);
        }
        return [];
      }),
    );
  }

  private applySorting(data: any[]): any[] {
    return data.sort((a, b) => {
      if (a.isPinned !== b.isPinned) {
        return Number(b.isPinned) - Number(a.isPinned);
      }
      const nameA = a.name ? a.name.toLowerCase() : '';
      const nameB = b.name ? b.name.toLowerCase() : '';

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
